import api from "@/api";
import events from "@/events";
import notify from "@/utils/notify";
import globals from "@/utils/globals";
import * as gtm from "@/services/tracking/gtm";
import { adjustUrl, deeplinkUrl as createDeeplinkUrl } from "@/utils/adjust-links";

const state = {
  fingerprint: null,
  segment: null,
  authenticityToken: null,
  loggedIn: null,
  hasTrialed: null,
  willRenew: null,
  plans: {
    items: [],
    yearlySavedPercents: null
  },
  abtests: {},
  supportedLanguages: globals.configSupportedLanguages,
  hasMobileApp: null,
  isIos: null,
  isAndroid: null,
  cookies: {
    submitted: null,
    consented: null,
    options: {
      necessary: true,
      preferences: false,
      statistics: false,
      marketing: false
    }
  },
  usersEmail: null,
  isOrganisationOwner: false,
  isOrganisationMember: false,
  isB2cMultiUserPlan: false
};

const getters = {
  abtests: state =>  state.abtests,
  authenticityToken: state => {
    return state.authenticityToken;
  },
  isAuthenticated: state => {
    return !!state.authenticityToken;
  },
  isAnonymous(state) {
    return state.segment === "anonymous";
  },
  hasUserAccessToContent: state => {
    // Users have full access to content if they are either:
    // - fully premium (segment = 'premium')
    // - or have some sort of "free access" e.g. through referral (segment = 'trial')
    return state.segment === "premium" || state.segment === "trial";
  },
  isUserPremium: state => {
    return state.segment === "premium";
  },
  isUserBasic: state => {
    return state.segment === "basic";
  },
  isUserLoggedOut: state => {
    return state.loggedIn === false;
  },
  isUserLoggedIn: state => {
    return state.loggedIn === true;
  },
  hasTrialed: state => {
    return state.hasTrialed === true;
  },
  willRenew: state => {
    return state.willRenew === true;
  },
  canTrial: (state) => {
    return state.hasTrialed === false;
  },
  supportedLanguages: state => {
    return state.supportedLanguages;
  },
  displayConsentBanner: state => {
    return state.cookies.submitted === false;
  },
  downloadAppUrl: state => (buttonCode, platform = null, androidWebToMobileFlag = null) => {
    if (!state.fingerprint) {
      return "#";
    }

    const baseConfig = {
      buttonCode,
      testCategories: state.abtests,
      fingerprint: state.fingerprint
    };

    if (platform === "ios" || (platform === null && state.isIos)) {
      return adjustUrl({ ...baseConfig, platform: "ios" });
    } else if (platform === "android" || (platform === null && state.isAndroid)) {
      return adjustUrl({ ...baseConfig, platform: "android", androidWebToMobileFlag });
    } else {
      return createDeeplinkUrl(baseConfig);
    }
  },
  deeplinkUrl: state => (deeplinkPath, buttonCode) => {
    if (!state.fingerprint) {
      return "#";
    }

    return createDeeplinkUrl({
      deeplinkPath,
      buttonCode,
      testCategories: state.abtests,
      fingerprint: state.fingerprint
    });
  },
  hasMobileApp: (state) => {
    return state.hasMobileApp;
  },
  usersEmail: (state) => {
    return state.usersEmail;
  },
  isOrganisationMember: (state) => {
    return state.isOrganisationMember;
  },
  isOrganisationOwner: (state) => {
    return state.isOrganisationOwner;
  },
  isB2cMultiUserPlan: (state) => {
    return state.isB2cMultiUserPlan;
  }
};

const mutations = {
  fingerprint: (state, payload) => {
    state.fingerprint = payload;
  },
  segment: (state, payload) => {
    state.segment = payload;
  },
  authenticityToken: (state, payload) => {
    state.authenticityToken = payload;
  },
  loggedIn: (state, payload) => {
    state.loggedIn = payload;
  },
  hasTrialed: (state, payload) => {
    state.hasTrialed = payload;
  },
  willRenew: (state, payload) => {
    state.willRenew = payload;
  },
  plans: (state, payload) => {
    state.plans.items = [...payload];
  },
  abtests: (state, payload) => {
    state.abtests = { ...payload };
  },
  yearlySavedPercents: (state, payload) => {
    state.plans.yearlySavedPercents = payload;
  },
  supportedLanguages: (state, payload) => {
    state.supportedLanguages = payload;
  },
  appStatus: (state, payload) => {
    state.hasMobileApp = payload.hasMobileApp;
    state.isIos = payload.isIos;
    state.isAndroid = payload.isAndroid;
  },
  cookiesSubmitted: (state, payload) => {
    state.cookies = {
      ...state.cookies,
      submitted: payload
    };
  },
  cookiesConsented: (state, payload) => {
    state.cookies = {
      ...state.cookies,
      consented: payload
    };
  },
  cookiesOptions: (state, payload) => {
    state.cookies.options = {
      ...state.cookies.options,
      ...payload
    };
  },
  usersEmail: (state, payload) => {
    state.usersEmail = payload;
  },
  isOrganisationOwner: (state, payload) => {
    state.isOrganisationOwner = payload;
  },
  isOrganisationMember: (state, payload) => {
    state.isOrganisationMember = payload;
  },
  isB2cMultiUserPlan: (state, payload) => {
    state.isB2cMultiUserPlan = payload;
  }
};

const actions = {
  setup: ({ commit }, payload) => {
    commit("fingerprint", payload.fingerprint);
    commit("segment", payload.segment);
    commit("authenticityToken", payload.authenticity_token);
    commit("loggedIn", !payload.guest);
    commit("supportedLanguages", payload.supported_languages);
    commit("hasTrialed", !!payload.has_trialed);
    commit("willRenew", !!payload.will_renew);
    commit("appStatus", {
      hasMobileApp: payload["has_mobile_app"],
      isIos: payload["is_ios"],
      isAndroid: payload["is_android"]
    });

    events.emitNoLegacy("setup:ready", payload);
    document.querySelector("body").classList.add("setup-ready");

    // These test categories include only those from cached pages, where mickey_mouse is called.
    // Non-cached pages trigger the categorization somewhere else, e.g. from the Rails controller.
    commit("abtests", payload.test_categories);
    events.emitNoLegacy("abtest:ready", payload.test_categories || {});
  },
  plans: ({ commit }) => {
    api.plans()
      .then(data => {
        commit("plans", data.plans);
        commit("yearlySavedPercents", data.yearlySavedPercents);
        events.emit("plans:ready", data);
      })
      .catch(error => {
        notify("Failed to fetch plans");
        events.emit("plans:error", error);
      });
  },
  organisationInfo: async ({ commit }) => {
    try {
      const data = await api.organisations.me();
      commit("isOrganisationMember", !!data.organisationId);
      commit("isOrganisationOwner", data.role === "owner");
      commit("isB2cMultiUserPlan", !!data.isB2cMultiUserPlan);
    } catch (error) {
      notify("Failed to fetch organisation info", error);
    }
  },
  loadUsersAccounts: async ({ commit }) => {
    commit("usersEmail", null);

    return api.settings.loadAccounts()
      .then((response) => {
        commit("usersEmail", response.accounts["primary"]);
      })
      .catch(() => {
        commit("usersEmail", null);
      });
  },
  initCookies: ({ commit }, payload) => {
    commit("cookiesSubmitted", true);
    commit("cookiesConsented", payload.consented);
    commit("cookiesOptions", payload.options);

    events.emit("cookies:ready");

    if (payload.options.preferences &&
      payload.options.statistics &&
      payload.options.marketing) {

      gtm.loadScript();
    }
  }
};

export default {
  state,
  getters,
  mutations,
  actions
};
